import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { AppState } from '@/store/store.ts';
import { useCheckSwapByTradeCaptureMutation } from '@/store/swap/api/index.ts';
import { swapErrorOccurred } from '@/store/swap/slice.ts';
import type { FxSwap, FxSwapRegularType } from '@/store/swap/state.ts';
import { getSwapTradeCaptureQueue } from '@/store/swap/swapTradeCaptureQueue.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

// ██████╗  █████╗ ████████╗ █████╗
// ██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
// ██║  ██║███████║   ██║   ███████║
// ██║  ██║██╔══██║   ██║   ██╔══██║
// ██████╔╝██║  ██║   ██║   ██║  ██║
// ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

export type SwapContextContent = {
  tileId: string;
  // swap: FxSwap;
  isChecking: boolean;
  checkSwapWithTradeCapture: (
    change: ObjectWithUndefinedPropertiesNullable<FxSwap['properties']>,
    // previous type is required to set correctly the body send to trade capture
    previousType?: FxSwapRegularType,
    isNonDeliverableOverride?: boolean,
  ) => Promise<void>;
};

export const SwapContext = createContext<SwapContextContent>({
  tileId: '',
  isChecking: false,
  checkSwapWithTradeCapture: async () => {},
});

// ██████╗ ██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗██████╗
// ██╔══██╗██╔══██╗██╔═══██╗██║   ██║██║██╔══██╗██╔════╝██╔══██╗
// ██████╔╝██████╔╝██║   ██║██║   ██║██║██║  ██║█████╗  ██████╔╝
// ██╔═══╝ ██╔══██╗██║   ██║╚██╗ ██╔╝██║██║  ██║██╔══╝  ██╔══██╗
// ██║     ██║  ██║╚██████╔╝ ╚████╔╝ ██║██████╔╝███████╗██║  ██║
// ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═══╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

export function SwapProvider(props: { tileId: string; children: ReactNode }) {
  const { tileId, children } = props;

  const dispatch = useAppDispatch();

  const [isChecking, setIsChecking] = useState(false);

  const isNonDeliverable = useAppSelector((state) => state.swap[props.tileId]?.isNonDeliverable);

  const [checkSwapByTradeCapture] = useCheckSwapByTradeCaptureMutation();

  const checkSwapWithTradeCapture = useCallback(
    async (
      properties: ObjectWithUndefinedPropertiesNullable<FxSwap['properties']>,
      _previousType?: FxSwapRegularType,
      isNonDeliverableOverride: boolean | undefined = undefined,
    ) => {
      // Add the call to the queue
      // If a request is already running, this one will wait until it completes
      return getSwapTradeCaptureQueue(tileId).add(async () => {
        if (isNotDefined(isNonDeliverable)) {
          return;
        }

        const isNonDeliverableNext =
          isNonDeliverableOverride !== undefined ? isNonDeliverableOverride : isNonDeliverable;

        // Timer to show "checking" state after 500ms
        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        try {
          await checkSwapByTradeCapture({
            tileId,
            isNonDeliverable: isNonDeliverableNext,
            isNonDeliverableChanged: isNonDeliverableOverride !== undefined,
            properties,
          })
            .unwrap()
            .catch((requestError: FetchBaseQueryError) => {
              if (requestError.status === 'CUSTOM_ERROR') {
                dispatch(
                  swapErrorOccurred({
                    tileId,
                    errorType: 'tile',
                    errorCode: 'NO_RESPONSE',
                    errorMessage: 'No response from trade capture',
                  }),
                );
                return;
              }

              dispatch(
                swapErrorOccurred({
                  tileId,
                  errorType: 'tile',
                  errorCode: 'TIMEOUT',
                  errorMessage:
                    'message' in requestError ? (requestError.message as string) : 'An unknown error occurred',
                }),
              );
            });
        } finally {
          // Clean up timer and state, even on error
          clearTimeout(checkTimer);
          setIsChecking(false);
        }
      });
    },
    [tileId, isNonDeliverable, checkSwapByTradeCapture],
  );

  if (isNotDefined(isNonDeliverable)) {
    throw new Error(`Could not find swap with tileId ${tileId}`);
  }

  const content: SwapContextContent = useMemo(() => {
    return {
      tileId,
      isChecking,
      checkSwapWithTradeCapture,
    };
  }, [checkSwapWithTradeCapture, isChecking, tileId]);

  return <SwapContext.Provider value={content}>{children}</SwapContext.Provider>;
}

// ██╗  ██╗ ██████╗  ██████╗ ██╗  ██╗███████╗
// ██║  ██║██╔═══██╗██╔═══██╗██║ ██╔╝██╔════╝
// ███████║██║   ██║██║   ██║█████╔╝ ███████╗
// ██╔══██║██║   ██║██║   ██║██╔═██╗ ╚════██║
// ██║  ██║╚██████╔╝╚██████╔╝██║  ██╗███████║
// ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function useSwapContext() {
  return useContext(SwapContext);
}

export function useSwapIsChecking() {
  return useContext(SwapContext).isChecking;
}

export function useCheckSwapByTradeCapture() {
  return useContext(SwapContext).checkSwapWithTradeCapture;
}

export function useSwapSelector<TSelectedContent>(selector: (swap: FxSwap, state: AppState) => TSelectedContent) {
  const { tileId } = useContext(SwapContext);

  const swap = useAppSelector((state) => state.swap[tileId]);

  if (isNotDefined(swap)) {
    throw new Error(`Could not find swap with tileId ${tileId}`);
  }

  return useAppSelector((state) => selector(swap, state), deepEqual);
}
